import { FacebookExport } from '../components/FacebookExport.js';
import { useState } from 'react';
import type { ChangeEvent, JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { date, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-broadcasts.css';

/**
 * Statické seznamy adres pro rozesílky (docs/ROZESILKY.md kap. 1). Seznam drží jen adresy; že adresa
 * smí dostat zprávu, se zjišťuje až v náhledu a při odeslání. Adresa bez kontaktu v hubu se neodešle.
 */

interface ListRow {
  id: string;
  name: string;
  description: string | null;
  source: string;
  sourceSegment: string | null;
  members: number;
  inHub: number;
  createdAt: string;
}

const SOURCE_LABEL: Record<string, string> = {
  csv: 'Soubor CSV',
  segment: 'Snímek segmentu',
  manual: 'Ručně zadané',
};

type Mode = 'csv' | 'segment' | 'manual';

export function StaticLists({ canWrite }: { canWrite: boolean }): JSX.Element {
  const client = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('csv');
  const [name, setName] = useState('');
  const [csv, setCsv] = useState('');
  const [manual, setManual] = useState('');
  const [segmentCode, setSegmentCode] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const [deleting, setDeleting] = useState<ListRow | null>(null);

  const query = useQuery({
    queryKey: ['static-lists'],
    queryFn: () => api.get<{ items: ListRow[] }>('/api/static-lists'),
  });
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () =>
      api.get<{ items: Array<{ code: string; name: string; memberCount: number }> }>(
        '/api/segments',
      ),
    enabled: mode === 'segment',
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; members: number; invalid?: number; duplicates?: number }>(
        '/api/static-lists',
        mode === 'csv'
          ? { source: 'csv', name, csv }
          : mode === 'segment'
            ? { source: 'segment', name, segmentCode }
            : {
                source: 'manual',
                name,
                emails: manual.split(/[\s,;]+/).filter((v) => v !== ''),
              },
      ),
    onSuccess: (r) => {
      setNote({
        tone: 'ok',
        text: `Seznam uložen: ${num(r.members)} adres${r.invalid ? `, ${num(r.invalid)} neplatných vynecháno` : ''}${r.duplicates ? `, ${num(r.duplicates)} duplicit sloučeno` : ''}.`,
      });
      setName('');
      setCsv('');
      setManual('');
      void client.invalidateQueries({ queryKey: ['static-lists'] });
    },
    onError: (err: Error) => setNote({ tone: 'bad', text: err.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/static-lists/${id}`),
    onSuccess: () => {
      setNote({ tone: 'ok', text: 'Seznam smazán.' });
      setDeleting(null);
      void client.invalidateQueries({ queryKey: ['static-lists'] });
    },
    onError: (err: Error) => {
      setNote({ tone: 'bad', text: err.message });
      setDeleting(null);
    },
  });

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    void file.text().then((text) => {
      setCsv(text);
      if (!name) setName(file.name.replace(/\.[^.]+$/, ''));
    });
  };

  if (query.isLoading) return <LoadingState what="statické seznamy" />;
  if (query.isError)
    return (
      <ErrorState
        title="Seznamy se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  if (openId) return <ListDetail id={openId} canWrite={canWrite} onBack={() => setOpenId(null)} />;

  const items = query.data?.items ?? [];
  const valid =
    name.trim() !== '' &&
    (mode === 'csv'
      ? csv.trim() !== ''
      : mode === 'segment'
        ? segmentCode !== ''
        : manual.trim() !== '');

  return (
    <>
      {note ? (
        <p className={`note${note.tone === 'bad' ? ' bad' : ''}`} role="status">
          {note.text}
        </p>
      ) : null}
      <Panel
        title="Statické seznamy"
        icon="users"
        subtitle="Pevné seznamy adres pro rozesílky: z CSV, ze snímku segmentu nebo ručně. Adresa v seznamu nic nezaručuje: při náhledu a odeslání se vždy ověří kontakt, souhlas a odhlášení."
      >
        {items.length === 0 ? (
          <EmptyState title="Zatím žádný statický seznam" icon="users">
            {canWrite
              ? 'Vytvořte první ve formuláři níže.'
              : 'Seznamy zakládá mailing specialista.'}
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Název</th>
                  <th>Původ</th>
                  <th className="n">Adres</th>
                  <th className="n">Má kontakt v hubu</th>
                  <th>Vytvořeno</th>
                  <th>
                    <span className="sr-only">Akce</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <b>{l.name}</b>
                    </td>
                    <td>
                      {SOURCE_LABEL[l.source] ?? l.source}
                      {l.sourceSegment ? <code> {l.sourceSegment}</code> : null}
                    </td>
                    <td className="n">{num(l.members)}</td>
                    <td className="n">
                      {num(l.inHub)}{' '}
                      {l.inHub < l.members ? (
                        <StatusBadge tone="warn" icon="alert">
                          {num(l.members - l.inHub)} bez kontaktu
                        </StatusBadge>
                      ) : null}
                    </td>
                    <td>{date(l.createdAt)}</td>
                    <td>
                      <div className="row">
                        <button className="btn" onClick={() => setOpenId(l.id)}>
                          Členové
                        </button>
                        {canWrite ? (
                          <FacebookExport
                            path={`/api/static-lists/${l.id}/export-facebook`}
                            label="Exportovat pro Facebook"
                          />
                        ) : null}
                        {canWrite ? (
                          <button className="btn dng" onClick={() => setDeleting(l)}>
                            Smazat
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {canWrite ? (
        <Panel title="Nový seznam" icon="upload">
          <div className="seg" role="tablist" aria-label="Zdroj seznamu">
            {(['csv', 'segment', 'manual'] as const).map((m) => (
              <button
                key={m}
                role="tab"
                aria-selected={mode === m}
                className={mode === m ? 'on' : ''}
                onClick={() => setMode(m)}
              >
                {SOURCE_LABEL[m]}
              </button>
            ))}
          </div>
          <div className="bcform">
            <Field label="Název seznamu">
              <input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
            </Field>
            {mode === 'csv' ? (
              <>
                <Field
                  label="Soubor CSV"
                  hint="Sloupec email (nebo první sloupec s @), čárka nebo středník. Neplatné adresy a duplicity se vynechají."
                >
                  <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} />
                </Field>
                <Field label="Nebo vložte text CSV">
                  <textarea rows={5} value={csv} onChange={(e) => setCsv(e.target.value)} />
                </Field>
              </>
            ) : null}
            {mode === 'segment' ? (
              <Field
                label="Segment"
                hint="Vznikne pevný snímek členů v tuto chvíli; pozdější členové do seznamu nepřibudou."
              >
                <select value={segmentCode} onChange={(e) => setSegmentCode(e.target.value)}>
                  <option value="">— vyberte —</option>
                  {(segments.data?.items ?? []).map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({num(s.memberCount)})
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {mode === 'manual' ? (
              <Field
                label="Adresy"
                hint="Oddělené mezerou, čárkou, středníkem nebo novým řádkem (nejvýš 5 000)."
              >
                <textarea rows={5} value={manual} onChange={(e) => setManual(e.target.value)} />
              </Field>
            ) : null}
          </div>
          <div className="row">
            <button
              className="btn pri"
              disabled={!valid || create.isPending}
              onClick={() => create.mutate()}
            >
              <Icon name="upload" size={14} /> Vytvořit seznam
            </button>
          </div>
        </Panel>
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Smazat seznam?"
        danger
        confirmLabel="Smazat seznam"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      >
        Seznam „{deleting?.name}“ se smaže i s adresami. Seznam, který používá rozesílka, smazat
        nejde.
      </ConfirmDialog>
    </>
  );
}

function ListDetail({
  id,
  canWrite,
  onBack,
}: {
  id: string;
  canWrite: boolean;
  onBack: () => void;
}): JSX.Element {
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [add, setAdd] = useState('');
  const [remove, setRemove] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  const query = useQuery({
    queryKey: ['static-list', id, search, offset],
    queryFn: () =>
      api.get<{
        list: ListRow;
        members: { total: number; items: Array<{ email: string; inHub: boolean }> };
      }>(
        `/api/static-lists/${id}?limit=50&offset=${offset}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      ),
  });
  const split = (v: string): string[] => v.split(/[\s,;]+/).filter((x) => x !== '');
  const update = useMutation({
    mutationFn: () =>
      api.put<{ members: number; added: number; removed: number; invalid: number }>(
        `/api/static-lists/${id}`,
        {
          ...(split(add).length > 0 ? { add: split(add) } : {}),
          ...(split(remove).length > 0 ? { remove: split(remove) } : {}),
        },
      ),
    onSuccess: (r) => {
      setNote({
        tone: 'ok',
        text: `Přidáno ${num(r.added)}, odebráno ${num(r.removed)}${r.invalid ? `, ${num(r.invalid)} neplatných vynecháno` : ''}. V seznamu je ${num(r.members)} adres.`,
      });
      setAdd('');
      setRemove('');
      void client.invalidateQueries({ queryKey: ['static-list', id] });
      void client.invalidateQueries({ queryKey: ['static-lists'] });
    },
    onError: (err: Error) => setNote({ tone: 'bad', text: err.message }),
  });

  if (query.isLoading) return <LoadingState what="členy seznamu" />;
  if (query.isError || !query.data)
    return (
      <ErrorState
        title="Seznam se nepodařilo načíst"
        error={query.error ?? new Error('Seznam neexistuje.')}
        onRetry={() => void query.refetch()}
      />
    );
  const { list, members } = query.data;

  return (
    <>
      <Panel
        title={list.name}
        icon="users"
        subtitle={`${SOURCE_LABEL[list.source] ?? list.source} · ${num(list.members)} adres, ${num(list.inHub)} má kontakt v hubu`}
        action={
          <button className="btn" onClick={onBack}>
            <Icon name="back" size={14} /> Všechny seznamy
          </button>
        }
      >
        {note ? (
          <p className={`note${note.tone === 'bad' ? ' bad' : ''}`} role="status">
            {note.text}
          </p>
        ) : null}
        <Field label="Hledat adresu">
          <input
            value={search}
            placeholder="část adresy"
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
          />
        </Field>
        {members.items.length === 0 ? (
          <EmptyState title="Žádné adresy" icon="users">
            Pro toto hledání nic nenalezeno.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Adresa (maskovaná)</th>
                  <th>Kontakt v hubu</th>
                </tr>
              </thead>
              <tbody>
                {members.items.map((m, i) => (
                  <tr key={`${m.email}-${i}`}>
                    <td>{m.email}</td>
                    <td>
                      {m.inHub ? (
                        <StatusBadge tone="ok" icon="check">
                          Má kontakt
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="warn" icon="alert">
                          Bez kontaktu (neodešle se)
                        </StatusBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row">
          <button
            className="btn"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Předchozí
          </button>
          <span className="hint">
            {num(Math.min(offset + 1, members.total))}–{num(Math.min(offset + 50, members.total))} z{' '}
            {num(members.total)}
          </span>
          <button
            className="btn"
            disabled={offset + 50 >= members.total}
            onClick={() => setOffset(offset + 50)}
          >
            Další
          </button>
        </div>
      </Panel>
      {canWrite ? (
        <Panel title="Přidat nebo odebrat adresy" icon="refresh">
          <div className="bcform">
            <Field label="Přidat adresy" hint="Oddělené mezerou, čárkou nebo novým řádkem.">
              <textarea rows={3} value={add} onChange={(e) => setAdd(e.target.value)} />
            </Field>
            <Field label="Odebrat adresy">
              <textarea rows={3} value={remove} onChange={(e) => setRemove(e.target.value)} />
            </Field>
          </div>
          <div className="row">
            <button
              className="btn pri"
              disabled={update.isPending || (split(add).length === 0 && split(remove).length === 0)}
              onClick={() => update.mutate()}
            >
              Uložit změny
            </button>
          </div>
        </Panel>
      ) : null}
    </>
  );
}
